import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormGroup, FormBuilder, ReactiveFormsModule, FormControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../shared/shared-imports';
import { environment } from '../../../../environments/environment';
import { faqData } from '../../../data/faq.data';

interface FaqItem {
  questionEn: string;
  answerEn: string;
  questionAr: string;
  answerAr: string;
  cat: string;
  qKey?: string;
  aKey?: string;
  open?: boolean;
}

interface FaqDataItem {
  category?: string;
  question_en?: string;
  answer_en?: string;
  question_ar?: string;
  answer_ar?: string;
  qKey?: string;
  aKey?: string;
}

interface FaqDataFile {
  faqs?: FaqDataItem[];
}
@Component({
  selector: 'app-faq',
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './faq.component.html',
  styleUrl: './faq.component.scss'
})
export class FaqComponent {
  /* ------------ filter form ------------ */
  form: FormGroup;
  // MVP1 flags (default to true when undefined in environment)
  faqDropDownEnabled = environment.mVP1Settings?.faqDropDownEnabled ?? true;

  categories = [
    { value: 'all', label: 'FAQ.CAT.ALL' },
    { value: 'general', label: 'FAQ.CAT.GENERAL' },
    { value: 'accounts', label: 'FAQ.CAT.ACCOUNTS' },
  ];

  faqs: FaqItem[] = this.mapFaqs(faqData as FaqDataFile);
  
  constructor(
    private fb: FormBuilder,
    private translate: TranslateService
  ) {
    this.form = this.fb.group({
      category: ['all'],
      search: [''],
    });
  }
  // This method is used to filter ed
  get filtered(): FaqItem[] {
    const { category, search } = this.form.value;
    const term = this.normalize(search as string);
    const activeCategory = category || 'all';

    return this.faqs.filter(f =>
      (activeCategory === 'all' || f.cat === activeCategory) &&
      (!term || this.matchesSearch(f, term))
    );
  }
  // This method is used to check whether cat using cat value
  hasCat(catValue: string): boolean {
    return this.filtered.some(f => f.cat === catValue);
  }
  // This method is used to toggle using f
  toggle(f: FaqItem) {
    for (const item of this.faqs) {
      item.open = item === f ? !item.open : false;
    }
  }

  get searchControl(): FormControl {
    return this.form.get('search') as FormControl;
  }

  displayQuestion(f: FaqItem): string {
    if (this.getLang() === 'ar') {
      return f.questionAr || f.questionEn;
    }
    return f.questionEn || f.questionAr;
  }

  displayAnswer(f: FaqItem): string {
    if (this.getLang() === 'ar') {
      return f.answerAr || f.answerEn;
    }
    return f.answerEn || f.answerAr;
  }

  private getLang(): string {
    return (this.translate.currentLang || localStorage.getItem('selectedLang') || 'en').toLowerCase();
  }

  private normalize(value: string): string {
    return (value || '').toLowerCase().trim();
  }

  private matchesSearch(f: FaqItem, term: string): boolean {
    const candidates = [
      f.questionEn,
      f.answerEn,
      f.questionAr,
      f.answerAr
    ];
    return candidates.some((text) => this.normalize(text).includes(term));
  }

  private mapFaqs(data: FaqDataFile): FaqItem[] {
    return (data?.faqs || []).map((item) => ({
      cat: item.category || 'general',
      questionEn: item.question_en || '',
      answerEn: item.answer_en || '',
      questionAr: item.question_ar || '',
      answerAr: item.answer_ar || '',
    }));
  }
}
